import React, { useState, useRef, useEffect, useCallback } from 'react';
import './OrganizationEarning.css';
import { Box, Typography, Avatar, Select, MenuItem } from '@mui/material';
import usePagination from '@mui/material/usePagination';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import SearchIcon from '@mui/icons-material/Search';
import KoinIcon from '../../../../assets/Admin/EarningIcons/koin-icons.svg';
import ReactDOM from 'react-dom';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';
import '../../SkeletonLoader/SkeletonLoader.css';
import OrganizationEarningsTableSkeleton from '../../../SkeletonEffect/OrganizationEarningsTableSkeleton/OrganizationEarningsTableSkeleton';

const OrganizationEarning = ({ organizationId }) => {
  const [activeFilter, setActiveFilter] = useState('this-month');
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedDateRange, setSelectedDateRange] = useState({ start: null, end: null });
  const [isSelectingStart, setIsSelectingStart] = useState(true);
  const [isCustomDateConfirmed, setIsCustomDateConfirmed] = useState(false);
  const customButtonRef = useRef(null);
  const [earningsData, setEarningsData] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(true);
  const [earningsError, setEarningsError] = useState(null);
  const [conversionRate, setConversionRate] = useState(7.5); // Default fallback
  const [conversionLoading, setConversionLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState(null);
  // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch conversion rate
  const fetchConversionRate = useCallback(async () => {
    try {
      setConversionLoading(true);
      const response = await axiosInstance.get('/auth/koins-to-riyal');
      setConversionRate(response.data.conversion_rate);
    } catch (error) {
      console.error('Error fetching conversion rate:', error);
      // Keep default value of 7.5 if API fails
    } finally {
      setConversionLoading(false);
    }
  }, []);

  // Fetch earnings data
  const fetchEarningsData = useCallback(async () => {
    if (!organizationId) {
      setEarningsLoading(false);
      return;
    }

    // Don't fetch if custom filter is active but not confirmed
    if (activeFilter === 'custom' && !isCustomDateConfirmed) {
      return;
    }

    try {
      setEarningsLoading(true);
      setEarningsError(null);

      const { start, end } = getDateRange();
      const params = {};
      if (start && end) {
        params.start_date = start;
        params.end_date = end;
      }
      const response = await axiosInstance.get(`/admin/organisation/earnings/${organizationId}`, {
        params,
      });
      setEarningsData(response.data);
    } catch (error) {
      console.error('Error fetching earnings data:', error);
      setEarningsError('Failed to load earnings data');
      setEarningsData(null);
    } finally {
      setEarningsLoading(false);
    }
  }, [organizationId, activeFilter, selectedDateRange, isCustomDateConfirmed]);

  // Fetch transactions data
  const fetchTransactions = useCallback(async () => {
    if (!organizationId) {
      setTransactions([]);
      setTransactionsLoading(false);
      setTotalCount(0);
      return;
    }

    // Don't fetch if custom filter is active but not confirmed
    if (activeFilter === 'custom' && !isCustomDateConfirmed) {
      return;
    }

    setTransactionsLoading(true);
    setTransactionsError(null);
    try {
      const { start, end } = getDateRange();
      const params = {
        search_query: searchQuery,
        page,
        page_size: rowsPerPage,
      };
      if (start && end) {
        params.start_date = start;
        params.end_date = end;
      }
      const response = await axiosInstance.get(
        `/admin/organisation/teachers/transactions/${organizationId}`,
        {
          params,
        }
      );
      setTransactions(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      setTransactionsError('Failed to load transactions');
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setTransactionsLoading(false);
    }
  }, [
    organizationId,
    activeFilter,
    selectedDateRange,
    searchQuery,
    isCustomDateConfirmed,
    page,
    rowsPerPage,
  ]);

  useEffect(() => {
    fetchConversionRate();
  }, [fetchConversionRate]);

  useEffect(() => {
    fetchEarningsData();
  }, [fetchEarningsData]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [
    activeFilter,
    searchQuery,
    selectedDateRange.start,
    selectedDateRange.end,
    isCustomDateConfirmed,
  ]);

  // Calculate koin values (assuming 1 Koin = $2 USD)
  const calculateKoins = amount => {
    if (!amount) return 0;

    // Handle both string and number inputs
    let numericAmount;
    if (typeof amount === 'string') {
      numericAmount = parseFloat(amount.replace(/[^0-9.-]+/g, ''));
    } else {
      numericAmount = parseFloat(amount);
    }

    return isNaN(numericAmount) ? 0 : Math.round(numericAmount / 2);
  };

  // Convert koins to riyal
  const convertKoinsToRiyal = koins => {
    if (!koins) return 0;
    const numericKoins = parseFloat(koins);
    return isNaN(numericKoins) ? 0 : Math.round(numericKoins * conversionRate);
  };

  // Mock data updated to match the image
  const earningsSummary = {
    totalEarning: earningsLoading
      ? '---'
      : earningsError
        ? '---'
        : earningsData?.total_earnings || '0',
    vestedEarning: earningsLoading
      ? '---'
      : earningsError
        ? '---'
        : earningsData?.vested_earnings || '0',
    availableForPayout: earningsLoading
      ? '---'
      : earningsError
        ? '---'
        : earningsData?.available_payout || '0',
    lastPayout: earningsLoading ? '---' : earningsError ? '---' : earningsData?.last_payout || '0',
    coinValue: 200,
  };

  // Calendar helper functions
  const getDaysInMonth = date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add days from previous month
    const prevMonth = new Date(year, month - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
      });
    }

    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }

    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }

    return days;
  };

  const handleDateSelect = date => {
    if (isSelectingStart) {
      setSelectedDateRange({ start: date, end: null });
      setIsSelectingStart(false);
    } else {
      if (date >= selectedDateRange.start) {
        setSelectedDateRange({ ...selectedDateRange, end: date });
        setIsSelectingStart(true);
      } else {
        // If end date is before start date, swap them
        setSelectedDateRange({ start: date, end: selectedDateRange.start });
        setIsSelectingStart(true);
      }
    }
  };

  const handleConfirmDateRange = () => {
    if (selectedDateRange.start && selectedDateRange.end) {
      setShowCalendar(false);
      setIsCustomDateConfirmed(true);
      // Here you would typically filter your data based on the date range
      console.log('Date range selected:', selectedDateRange);
    }
  };

  const handleCancelDateRange = () => {
    setSelectedDateRange({ start: null, end: null });
    setIsSelectingStart(true);
    setIsCustomDateConfirmed(false);
    setShowCalendar(false);
  };

  const isDateInRange = date => {
    if (!selectedDateRange.start || !selectedDateRange.end) return false;
    return date >= selectedDateRange.start && date <= selectedDateRange.end;
  };

  const isDateSelected = date => {
    if (!selectedDateRange.start) return false;
    if (selectedDateRange.end) {
      return (
        date.getTime() === selectedDateRange.start.getTime() ||
        date.getTime() === selectedDateRange.end.getTime()
      );
    }
    return date.getTime() === selectedDateRange.start.getTime();
  };

  const handlePrevMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        showCalendar &&
        customButtonRef.current &&
        !customButtonRef.current.contains(event.target)
      ) {
        const calendarElement = document.querySelector('.org-custom-calendar');
        if (calendarElement && !calendarElement.contains(event.target)) {
          setShowCalendar(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  // Render custom calendar
  const renderCustomCalendar = () => {
    if (!showCalendar) return null;

    const days = getDaysInMonth(calendarDate);
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const calendar = (
      <div className="org-custom-calendar">
        <div className="org-calendar-header">
          <button onClick={handlePrevMonth} className="org-calendar-nav-btn">
            &lt;
          </button>
          <div className="org-calendar-title">
            {`${monthNames[calendarDate.getMonth()]} ${calendarDate.getFullYear()}`}
          </div>
          <button onClick={handleNextMonth} className="org-calendar-nav-btn">
            &gt;
          </button>
        </div>

        <div className="org-calendar-days-header">
          <div>S</div>
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
        </div>

        <div className="org-calendar-grid">
          {days.map((day, index) => (
            <div
              key={index}
              className={`org-calendar-day ${!day.isCurrentMonth ? 'other-month' : ''}
                         ${isDateSelected(day.date) ? 'selected' : ''}
                         ${isDateInRange(day.date) ? 'in-range' : ''}`}
              onClick={() => handleDateSelect(day.date)}
            >
              {day.day}
            </div>
          ))}
        </div>

        <div className="org-calendar-actions">
          <button onClick={handleCancelDateRange} className="org-calendar-cancel-btn">
            Cancel
          </button>
          <button
            onClick={handleConfirmDateRange}
            className="org-calendar-confirm-btn"
            disabled={!selectedDateRange.start || !selectedDateRange.end}
          >
            Confirm
          </button>
        </div>
      </div>
    );

    return ReactDOM.createPortal(calendar, document.body);
  };

  // Helper to format date as YYYY-MM-DD
  const formatDate = date => {
    if (!date) return '';
    const d = new Date(date);
    const month = '' + (d.getMonth() + 1);
    const day = '' + d.getDate();
    const year = d.getFullYear();
    return [year, month.padStart(2, '0'), day.padStart(2, '0')].join('-');
  };

  // Format text: capitalize first letter and replace _ with space
  const formatText = text => {
    if (!text || typeof text !== 'string') return '';
    const formatted = text.replace(/_/g, ' ');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  // Calculate start and end date based on filter
  const getDateRange = () => {
    const today = new Date();
    let start, end;
    switch (activeFilter) {
      case 'this-week': {
        start = new Date(today);
        start.setDate(today.getDate() - 7);
        end = today;
        break;
      }
      case 'this-month': {
        start = new Date(today);
        start.setMonth(today.getMonth() - 1);
        end = today;
        break;
      }
      case '6-month': {
        start = new Date(today);
        start.setMonth(today.getMonth() - 6);
        end = today;
        break;
      }
      case '1-year': {
        start = new Date(today);
        start.setFullYear(today.getFullYear() - 1);
        end = today;
        break;
      }
      case 'custom': {
        start = selectedDateRange.start;
        end = selectedDateRange.end;
        break;
      }
      default: {
        start = null;
        end = null;
      }
    }
    return { start: formatDate(start), end: formatDate(end) };
  };

  // Download handler
  const handleDownload = async () => {
    const { start, end } = getDateRange();
    if (!start || !end) {
      toast.error('Please select a valid date range.');
      return;
    }
    try {
      const response = await axiosInstance.get(
        `/admin/organisation/${organizationId}/summary-report/download`,
        {
          params: {
            start_date: start,
            end_date: end,
          },
        }
      );
      // The response is just the file link as a string
      const fileLink = response.data;
      if (fileLink) {
        window.open(fileLink, '_blank');
        toast.success('Downloaded');
      } else {
        toast.error('File link not found in response.');
      }
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download file.');
    }
  };

  // Pagination handlers and derived values
  const handleRowsPerPageChange = event => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const { items: paginationItems } = usePagination({
    count: totalPages,
    page,
    onChange: handlePageChange,
  });

  return (
    <Box className="org-earnings-container">
      {/* Filter section */}
      <Box className="org-earnings-filter-container">
        <Box className="org-earnings-search-bar">
          <SearchIcon className="org-earnings-search-icon" />
          <input
            type="text"
            placeholder="Search Instructor by Name"
            className="org-earnings-search-input"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') fetchTransactions();
            }}
          />
        </Box>
        <Box className="org-earnings-period-filter">
          <button
            className={`org-period-filter-btn ${activeFilter === 'this-week' ? 'active' : ''}`}
            onClick={() => {
              setShowCalendar(false);
              setActiveFilter(prev => (prev === 'this-week' ? null : 'this-week'));
            }}
          >
            This Week
          </button>
          <button
            className={`org-period-filter-btn ${activeFilter === 'this-month' ? 'active' : ''}`}
            onClick={() => {
              setShowCalendar(false);
              setActiveFilter(prev => (prev === 'this-month' ? null : 'this-month'));
            }}
          >
            This Month
          </button>
          <button
            className={`org-period-filter-btn ${activeFilter === '6-month' ? 'active' : ''}`}
            onClick={() => {
              setShowCalendar(false);
              setActiveFilter(prev => (prev === '6-month' ? null : '6-month'));
            }}
          >
            6 Month
          </button>
          <button
            className={`org-period-filter-btn ${activeFilter === '1-year' ? 'active' : ''}`}
            onClick={() => {
              setShowCalendar(false);
              setActiveFilter(prev => (prev === '1-year' ? null : '1-year'));
            }}
          >
            1 Year
          </button>
          <button
            ref={customButtonRef}
            className={`org-period-filter-btn ${activeFilter === 'custom' ? 'active' : ''}`}
            onClick={() => {
              if (activeFilter === 'custom') {
                setShowCalendar(false);
                setIsCustomDateConfirmed(false);
                setSelectedDateRange({ start: null, end: null });
                setActiveFilter(null);
              } else {
                setActiveFilter('custom');
                setIsCustomDateConfirmed(false);
                setShowCalendar(true);
              }
            }}
          >
            Custom
          </button>
        </Box>
      </Box>

      {/* Earnings summary cards */}
      <Box className="org-flex org-flex-wrap org-gap-4">
        <Box className="org-total-earning-container">
          <div className="org-total-earning-text">Total Earning</div>
          <Box className="org-flex org-gap-3 org-flex-col">
            <div className="org-total-amount-value">
              {earningsLoading ? (
                <div className="org-earn-skeleton org-earn-skeleton--amount" />
              ) : earningsError ? (
                '---'
              ) : (
                `${convertKoinsToRiyal(earningsData?.total_earnings || 0)} Riyal`
              )}
            </div>
            <Box className="org-flex org-gap-2">
              <img src={KoinIcon} alt="Koin" width="20" height="20" />
              <div className="org-koin-amount">
                {earningsLoading ? (
                  <div className="org-earn-skeleton org-earn-skeleton--koin" />
                ) : earningsError ? (
                  '---'
                ) : (
                  earningsData?.total_earnings || 0
                )}
              </div>
            </Box>
          </Box>
        </Box>

        <Box className="org-locked-earning-container">
          <div className="org-locked-earning-text">Vested Earning</div>
          <div className="org-locked-amount-value">
            {earningsLoading ? (
              <div className="org-earn-skeleton org-earn-skeleton--amount" />
            ) : earningsError ? (
              '---'
            ) : (
              `${convertKoinsToRiyal(earningsData?.vested_earnings || 0)} Riyal`
            )}
          </div>
          <Box className="org-flex org-items-center org-gap-2">
            <img src={KoinIcon} alt="Koin" width="20" height="20" />
            <div className="org-koin-amount">
              {earningsLoading ? (
                <div className="org-earn-skeleton org-earn-skeleton--koin" />
              ) : earningsError ? (
                '---'
              ) : (
                earningsData?.vested_earnings || 0
              )}
            </div>
          </Box>
        </Box>

        <Box className="org-available-payout-container">
          <div className="org-available-payout-text">Available for Payout</div>
          <div className="org-available-payout-value">
            {earningsLoading ? (
              <div className="org-earn-skeleton org-earn-skeleton--amount" />
            ) : earningsError ? (
              '---'
            ) : (
              `${convertKoinsToRiyal(earningsData?.available_payout || 0)} Riyal`
            )}
          </div>
          <Box className="org-flex org-items-center org-gap-2">
            <img src={KoinIcon} alt="Koin" width="20" height="20" />
            <div className="org-available-payout-koin">
              {earningsLoading ? (
                <div className="org-earn-skeleton org-earn-skeleton--koin" />
              ) : earningsError ? (
                '---'
              ) : (
                earningsData?.available_payout || 0
              )}
            </div>
          </Box>
        </Box>

        <Box className="org-locked-earning-container">
          <div className="org-locked-earning-text">Last Payout</div>
          <div className="org-locked-amount-value">
            {earningsLoading ? (
              <div className="org-earn-skeleton org-earn-skeleton--amount" />
            ) : earningsError ? (
              '---'
            ) : (
              `${convertKoinsToRiyal(earningsData?.last_payout || 0)} Riyal`
            )}
          </div>
          <Box className="org-flex org-items-center org-gap-2">
            <img src={KoinIcon} alt="Koin" width="20" height="20" />
            <div className="org-koin-amount">
              {earningsLoading ? (
                <div className="org-earn-skeleton org-earn-skeleton--koin" />
              ) : earningsError ? (
                '---'
              ) : (
                earningsData?.last_payout || 0
              )}
            </div>
          </Box>
        </Box>
      </Box>

      {/* Sessions table */}
      {transactionsLoading ? (
        <OrganizationEarningsTableSkeleton />
      ) : (
        <Box className="org-earnings-table-scroll-container">
          <Box className="org-earnings-table-wrapper">
            <table className="org-earnings-table">
              <thead>
                <tr>
                  <th className="org-earnings-th">Instructor</th>
                  <th className="org-earnings-th">Type</th>
                  <th className="org-earnings-th">Session Type</th>
                  <th className="org-earnings-th">Learner</th>
                  <th className="org-earnings-th">Date</th>
                  <th className="org-earnings-th">Duration</th>
                  <th className="org-earnings-th">Recording</th>
                  <th className="org-earnings-th">Koin</th>
                  <th className="org-earnings-th">Payment</th>
                </tr>
              </thead>
              <tbody>
                {transactionsError ? (
                  <tr>
                    <td colSpan={9} className="org-earnings-error-cell">
                      {transactionsError}
                    </td>
                  </tr>
                ) : totalCount === 0 ? (
                  <tr>
                    <td colSpan={9} className="org-earnings-empty-cell">
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  transactions.map(tx => (
                    <tr key={tx.id} className="org-earnings-row">
                      <td className="org-earnings-td org-instructor-cell">
                        <Avatar className="org-instructor-avatar">
                          {tx.teacher_name?.charAt(0)}
                        </Avatar>
                        <span className="org-instructor-name">{tx.teacher_name}</span>
                      </td>
                      <td className="org-earnings-td">
                        <span className="org-instructor-type">{formatText(tx.teacher_type)}</span>
                      </td>
                      <td className="org-earnings-td">
                        <Box
                          className={`org-session-type ${tx.session_type?.toLowerCase().replace(/_/g, '-')}`}
                        >
                          {formatText(tx.session_type)}
                        </Box>
                      </td>
                      <td className="org-earnings-td org-learner-cell">
                        {tx.students && tx.students.length > 1 ? (
                          <Box className="org-group-avatar">
                            <Box className="org-group-avatars">
                              {tx.students.slice(0, 2).map((student, idx) => (
                                <Avatar key={student.id} className="org-learner-avatar-small">
                                  {(student.first_name?.charAt(0) || '') +
                                    (student.last_name?.charAt(0) || '')}
                                </Avatar>
                              ))}
                            </Box>
                            <span className="org-group-names">
                              {tx.students
                                .map(student =>
                                  `${student.first_name || ''} ${student.last_name || ''}`.trim()
                                )
                                .join(', ')}
                            </span>
                          </Box>
                        ) : tx.students && tx.students.length === 1 ? (
                          <Box className="org-learner-single">
                            <Avatar className="org-learner-avatar">
                              {(tx.students[0].first_name?.charAt(0) || '') +
                                (tx.students[0].last_name?.charAt(0) || '')}
                            </Avatar>
                            <span className="org-learner-name">
                              {`${tx.students[0].first_name || ''} ${tx.students[0].last_name || ''}`.trim()}
                            </span>
                          </Box>
                        ) : (
                          <span className="org-no-learner">-</span>
                        )}
                      </td>
                      <td className="org-earnings-td">
                        <span className="org-session-date">
                          {tx.session_date ? new Date(tx.session_date).toLocaleDateString() : '-'}
                        </span>
                      </td>
                      <td className="org-earnings-td">
                        <span className="org-session-duration">
                          {tx.session_duration ? `${tx.session_duration} Minutes` : '-'}
                        </span>
                      </td>
                      <td className="org-earnings-td">
                        <span
                          className={tx.recording_url ? 'org-recording-yes' : 'org-recording-no'}
                        >
                          {tx.recording_url ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="org-earnings-td">
                        <span
                          className={`org-amount-text ${
                            tx.koins >= 0 ? 'org-amount-positive' : 'org-amount-negative'
                          }`}
                        >
                          {tx.koins}
                        </span>
                      </td>
                      <td className="org-earnings-td">
                        <span className={`org-payment-type ${tx.payment_type}`}>
                          {formatText(tx.payment_type)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      {/* Pagination */}
      {totalCount > 0 && (
        <Box
          className="org-ear-pagination-container"
          sx={{ marginTop: '0 !important', padding: '8px 16px !important' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <Typography className="org-ear-pagination-text">Showing</Typography>
            <Select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              className="org-ear-pagination-select"
              sx={{
                height: '30px',
                minWidth: '60px',
                fontFamily: 'Poppins',
                fontSize: '12px',
                margin: '0 4px',
                '& .MuiSelect-select': {
                  padding: '6px 24px 6px 8px',
                  textAlign: 'center',
                  fontFamily: 'Poppins',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                  color: '#000',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-icon': {
                  right: '4px',
                  color: '#666',
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={15}>15</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
            <Typography className="org-ear-pagination-text">
              of {totalCount} Transactions
            </Typography>
            {transactionsLoading && (
              <Typography className="org-ear-pagination-text" sx={{ marginLeft: 1 }}>
                Updating...
              </Typography>
            )}
          </Box>
          {paginationItems.length > 0 && (
            <nav>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '10px 0',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {paginationItems.map(
                  ({ page: itemPage, type, selected, disabled, ...item }, index) => {
                    let children = null;
                    if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                      children = <span className="org-ear-pagination-ellipsis">…</span>;
                    } else if (type === 'page') {
                      children = (
                        <button
                          type="button"
                          className="org-ear-pagination-button"
                          style={{
                            fontWeight: selected ? 'bold' : undefined,
                            margin: '0 5px',
                            padding: '5px 10px',
                            cursor: 'pointer',
                            backgroundColor: selected ? '#f0f0f0' : 'transparent',
                            border: 'none',
                            borderRadius: '4px',
                          }}
                          {...item}
                        >
                          {itemPage}
                        </button>
                      );
                    } else {
                      children = (
                        <button
                          type="button"
                          className="org-ear-pagination-button"
                          style={{
                            margin: '0 5px',
                            padding: '5px 10px',
                            cursor: 'pointer',
                            border: 'none',
                            backgroundColor: 'transparent',
                          }}
                          disabled={disabled}
                          {...item}
                        >
                          {type === 'previous' ? 'Previous' : 'Next'}
                        </button>
                      );
                    }
                    return <li key={index}>{children}</li>;
                  }
                )}
              </ul>
            </nav>
          )}
        </Box>
      )}

      {/* Download button */}
      <Box className="org-download-button-container">
        <button
          className={`org-download-button${transactions.length === 0 ? ' org-download-button-disabled' : ''}`}
          onClick={handleDownload}
          disabled={transactions.length === 0}
        >
          Download <FileDownloadOutlinedIcon fontSize="small" />
        </button>
      </Box>

      {/* Render custom calendar */}
      {renderCustomCalendar()}
    </Box>
  );
};

export default OrganizationEarning;
